import { supabase } from '../lib/supabase';
import { groupService } from './groupService';
import { notificationService } from './notificationService';

/**
 * Announcement Service
 * Chairs publish in-app announcements (one-off or pinned FAQ/notes) to an
 * audience defined as a union or intersection of participant groups. Delivery
 * reuses notifications/notification_recipients so messages land in the bell and
 * the notifications inbox; the announcement row keeps the record + pin state.
 */

const mapAnnouncement = (a) => ({
  id: a?.id,
  conferenceId: a?.conference_id,
  title: a?.title,
  body: a?.body,
  groupIds: a?.group_ids || [],
  audienceOp: a?.audience_op,
  isPinned: a?.is_pinned,
  notificationId: a?.notification_id,
  publishedAt: a?.published_at,
  conferenceName: a?.conferences?.name,
  recipientCount: a?.notifications?.total_recipients ?? null
});

export const announcementService = {
  /**
   * Resolve the target user_ids for a set of groups under union/intersection.
   */
  async resolveAudience(conferenceId, groupIds = [], op = 'union') {
    if (!groupIds?.length) return [];

    const { data: groups, error } = await supabase
      ?.from('conference_participant_groups')
      ?.select('*')
      ?.in('id', groupIds);
    if (error) throw error;

    const sets = [];
    for (const g of groups || []) {
      const group = { id: g?.id, roleSources: g?.role_sources || [] };
      sets.push(await groupService.resolveGroupUserIds(conferenceId, group));
    }
    if (!sets?.length) return [];

    let result;
    if (op === 'intersection') {
      result = sets.reduce((acc, s) =>
        new Set([...acc].filter(id => s.has(id))));
    } else {
      result = new Set();
      sets.forEach(s => s.forEach(id => result.add(id)));
    }
    return Array.from(result);
  },

  /**
   * Publish an announcement: resolve audience, deliver as an in-app
   * notification, and store the announcement record.
   */
  async publish({ conferenceId, title, body, groupIds, audienceOp = 'union', isPinned = false }) {
    try {
      const userIds = await this.resolveAudience(conferenceId, groupIds, audienceOp);
      if (!userIds?.length) {
        throw new Error('The selected audience has no recipients.');
      }

      const { id: notificationId } = await notificationService.createNotification({
        conferenceId,
        notificationType: 'custom',
        subject: title,
        message: body,
        recipientType: 'specific_users',
        recipientFilter: { userIds },
        sendImmediately: true
      });

      const { data: { user } } = await supabase?.auth?.getUser();
      const { data, error } = await supabase
        ?.from('conference_announcements')
        ?.insert({
          conference_id: conferenceId,
          title,
          body,
          group_ids: groupIds,
          audience_op: audienceOp,
          is_pinned: isPinned,
          notification_id: notificationId,
          created_by: user?.id
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return { ...mapAnnouncement(data), recipientCount: userIds?.length };
    } catch (error) {
      console.error('Publish announcement error:', error);
      throw error;
    }
  },

  async getAnnouncements(conferenceId) {
    try {
      const { data, error } = await supabase
        ?.from('conference_announcements')
        ?.select('*, notifications (total_recipients)')
        ?.eq('conference_id', conferenceId)
        ?.order('is_pinned', { ascending: false })
        ?.order('published_at', { ascending: false });

      if (error) throw error;
      return data?.map(mapAnnouncement) || [];
    } catch (error) {
      console.error('Get announcements error:', error);
      throw error;
    }
  },

  /** Edit title/body/pin. Keeps the delivered notification text in sync. */
  async updateAnnouncement(id, { title, body, isPinned }) {
    try {
      const { data, error } = await supabase
        ?.from('conference_announcements')
        ?.update({
          title,
          body,
          is_pinned: isPinned,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', id)
        ?.select('*, notifications (id)')
        ?.single();

      if (error) throw error;

      if (data?.notification_id) {
        await supabase
          ?.from('notifications')
          ?.update({ subject: title, message: body })
          ?.eq('id', data?.notification_id);
      }
      return mapAnnouncement(data);
    } catch (error) {
      console.error('Update announcement error:', error);
      throw error;
    }
  },

  async setPinned(id, isPinned) {
    try {
      const { error } = await supabase
        ?.from('conference_announcements')
        ?.update({ is_pinned: isPinned, updated_at: new Date()?.toISOString() })
        ?.eq('id', id);
      if (error) throw error;
    } catch (error) {
      console.error('Set pinned error:', error);
      throw error;
    }
  },

  /**
   * Delete an announcement and the notification it produced (which cascade-
   * removes it from recipients' inboxes).
   */
  async deleteAnnouncement(id, notificationId) {
    try {
      const { error } = await supabase
        ?.from('conference_announcements')
        ?.delete()
        ?.eq('id', id);
      if (error) throw error;

      if (notificationId) {
        await supabase?.from('notifications')?.delete()?.eq('id', notificationId);
      }
    } catch (error) {
      console.error('Delete announcement error:', error);
      throw error;
    }
  },

  /**
   * Pinned announcements the current user has received — for the FAQ/notes
   * section in the notifications inbox. RLS limits rows to ones the user got.
   */
  async getPinnedForUser() {
    try {
      const { data, error } = await supabase
        ?.from('conference_announcements')
        ?.select('*, conferences (name)')
        ?.eq('is_pinned', true)
        ?.order('published_at', { ascending: false });

      if (error) throw error;
      return data?.map(mapAnnouncement) || [];
    } catch (error) {
      console.error('Get pinned announcements error:', error);
      return [];
    }
  }
};

export default announcementService;
